import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from './firebase';
import { getAllAssessments, getEssaysForAssessment } from './assessmentService';
import { DEFAULT_MODEL_ID, MODELS } from '../constants';

const snapshotsCollection = collection(db, 'snapshots');

const normalizeCriterionName = (name: string) => (typeof name === 'string' ? name.trim().toLowerCase() : '');

const CSV_CRITERIA_LIMIT = 4;
const CSV_NA_VALUE = 'N/a';

const hasValue = (value: any) => value !== null && value !== undefined && value !== '';
const formatSnapshotValue = (value: any) => (hasValue(value) ? value : CSV_NA_VALUE);

const buildAssessmentLabel = (assessment: any) => {
  const name = assessment?.name || assessment?.title || assessment?.id || 'Assessment';
  const number = assessment?.assessmentNumber ?? 'N/A';
  return `Assessment ${number}: ${name}`;
};

const buildEssayLabel = (essay: any) => {
  const label = essay?.studentName || essay?.id || '';
  return label;
};

const toNumber = (value: any) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? NaN : parsed;
  }
  return NaN;
};

const computeDifference = (aiScore: any, teacherScore: any) => {
  const aiNum = toNumber(aiScore);
  const teacherNum = toNumber(teacherScore);
  if (!Number.isFinite(aiNum) || !Number.isFinite(teacherNum)) return CSV_NA_VALUE;
  return aiNum - teacherNum;
};

const getLatestCriteriaResults = (rawRuns: any[]) => {
  if (!Array.isArray(rawRuns) || rawRuns.length === 0) return [];
  const rawRunWithCriteria = [...rawRuns].reverse().find((r: any) => Array.isArray(r?.criteriaResults) && r.criteriaResults.length > 0);
  return rawRunWithCriteria?.criteriaResults || [];
};

const getCriterionOrder = (assessment: any, consolidatedCriteria: any[], manualResults: any[]) => {
  const assessmentCriteria = Array.isArray(assessment?.criteria)
    ? assessment.criteria.map((c: any) => c?.name).filter(Boolean)
    : [];
  if (assessmentCriteria.length > 0) return assessmentCriteria;

  const consolidatedNames = Array.isArray(consolidatedCriteria)
    ? consolidatedCriteria.map((c: any) => c?.criterionName).filter(Boolean)
    : [];
  if (consolidatedNames.length > 0) return consolidatedNames;

  const manualNames = Array.isArray(manualResults)
    ? manualResults.map((m: any) => m?.criterionName).filter(Boolean)
    : [];
  return manualNames;
};

const buildCriteriaBands = ({
  criterionOrder,
  consolidatedCriteria,
  rawCriteriaResults,
  manualResults,
  limit,
}: {
  criterionOrder: string[];
  consolidatedCriteria: any[];
  rawCriteriaResults: any[];
  manualResults: any[];
  limit: number;
}) => {
  const bands: Array<{ aiBand: string; teacherBand: string }> = [];
  const safeOrder = Array.isArray(criterionOrder) ? criterionOrder : [];

  const formatBandForCsv = (value: any) => {
    if (!hasValue(value)) return CSV_NA_VALUE;
    const str = String(value).trim();
    if (str === '') return CSV_NA_VALUE;
    // Prevent Excel from converting ranges like 11-15 into dates (11-Dec)
    if (/^\d+\s*-\s*\d+$/.test(str)) {
      return `'${str}`;
    }
    return str;
  };

  for (let i = 0; i < limit; i++) {
    const criterionName = safeOrder[i];
    if (!criterionName) {
      bands.push({ aiBand: CSV_NA_VALUE, teacherBand: CSV_NA_VALUE });
      continue;
    }
    const normalized = normalizeCriterionName(criterionName);
    const consolidated = (consolidatedCriteria || []).find((c: any) => normalizeCriterionName(c?.criterionName) === normalized);
    const raw = (rawCriteriaResults || []).find((c: any) => normalizeCriterionName(c?.criterionName) === normalized);
    const manual = (manualResults || []).find((m: any) => normalizeCriterionName(m?.criterionName) === normalized);

    const aiBand = consolidated?.selectedBand || raw?.selectedBand || '';
    const teacherBand = manual?.band ?? '';

    bands.push({
      aiBand: formatBandForCsv(aiBand),
      teacherBand: formatBandForCsv(teacherBand),
    });
  }

  return bands;
};

const buildLegacyModelResult = (essay: any, legacyModelId: string) => {
  if (!essay) return null;
  const hasLegacyData =
    essay.finalOverallScore !== undefined ||
    essay.finalOverallBand !== undefined ||
    (Array.isArray(essay.consolidatedMarkingResults) && essay.consolidatedMarkingResults.length > 0) ||
    (Array.isArray(essay.rawMarkingRuns) && essay.rawMarkingRuns.length > 0);
  if (!hasLegacyData) return null;

  return {
    modelId: legacyModelId,
    modelName: MODELS[legacyModelId]?.name || legacyModelId,
    rawMarkingRuns: Array.isArray(essay.rawMarkingRuns) ? essay.rawMarkingRuns : [],
    consolidatedMarkingResults: Array.isArray(essay.consolidatedMarkingResults) ? essay.consolidatedMarkingResults : [],
    finalOverallScore: essay.finalOverallScore ?? null,
    finalOverallBand: essay.finalOverallBand ?? null,
    feedback: essay.feedback ?? null,
  };
};

const resolveModelResult = (essay: any, selectedModelId?: string) => {
  if (!essay) return { modelResult: null, modelId: selectedModelId };

  const modelResults = essay.modelResults || {};
  if (selectedModelId) {
    if (modelResults[selectedModelId]) {
      return { modelResult: modelResults[selectedModelId], modelId: selectedModelId };
    }

    const legacyModelId = essay.modelUsed || DEFAULT_MODEL_ID;
    if (selectedModelId === legacyModelId) {
      const legacyResult = buildLegacyModelResult(essay, legacyModelId);
      return { modelResult: legacyResult, modelId: legacyModelId };
    }

    return { modelResult: null, modelId: selectedModelId };
  }

  const fallbackModelId = Object.keys(modelResults)[0] || essay.modelUsed || DEFAULT_MODEL_ID;
  const fallbackResult = modelResults[fallbackModelId] || buildLegacyModelResult(essay, fallbackModelId);
  return { modelResult: fallbackResult, modelId: fallbackModelId };
};

const deriveConsolidatedFromRaw = (rawCriteriaResults: any[]) => {
  if (!Array.isArray(rawCriteriaResults)) return [];
  return rawCriteriaResults.map((c: any) => ({
    criterionName: c?.criterionName || 'Criterion',
    selectedBand: c?.selectedBand || (Array.isArray(c?.bandAnalysis) && c.bandAnalysis[0]?.bandName) || '',
    score: c?.score ?? '',
  }));
};

const toDate = (value: any): Date | null => {
  try {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'object' && 'seconds' in value) return new Date((value as any).seconds * 1000);
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  } catch (err) {
    console.warn('Failed to parse date', value, err);
  }
  return null;
};

const formatDate = (value: any) => {
  const d = toDate(value);
  return d ? d.toISOString() : '';
};

export interface SnapshotMeta {
  id: string;
  userId: string;
  assessmentIds: string[];
  assessmentNames: string[];
  modelIds: string[];
  rowCount: number;
  triggeredBy: string;
  createdAt: any;
  selectedModelId?: string;
  format?: 'csv' | 'xlsx';
}

export interface SnapshotExportOptions {
  format?: 'csv' | 'xlsx';
  selectedModelId?: string;
}

const buildSnapshotData = async (
  userId: string,
  assessmentIds: string[],
  onStatus?: (msg: string) => void
) => {
  const allAssessments = await getAllAssessments(userId);
  const selectedAssessments = (allAssessments || []).filter((a: any) => assessmentIds.includes(a.id));

  const rows: any[] = [];
  const summaryRows: any[] = [];
  const modelIdsUsed = new Set<string>();

  for (const assessment of selectedAssessments) {
    onStatus?.(`Fetching essays for ${assessment.name || assessment.title || assessment.id}...`);
    const essays = await getEssaysForAssessment(userId, assessment.id);
    const assessmentLabel = assessment.name || assessment.title || assessment.id;

    for (const essay of essays || []) {
      const modelResults = essay.modelResults || {};
      const modelIds = Object.keys(modelResults);

      for (const modelId of modelIds) {
        const modelResult = modelResults[modelId];
        if (!modelResult) continue;

        modelIdsUsed.add(modelId);
        const modelConfig = MODELS[modelId];
        const rawRuns = Array.isArray(modelResult.rawMarkingRuns) ? modelResult.rawMarkingRuns : Array.isArray(essay.rawMarkingRuns) ? essay.rawMarkingRuns : [];
        const rawRunWithCriteria = [...rawRuns].reverse().find((r: any) => r?.criteriaResults?.length);
        const rawCriteriaResults = rawRunWithCriteria?.criteriaResults || [];
        let consolidatedCriteria = modelResult.consolidatedMarkingResults || [];
        if ((!consolidatedCriteria || consolidatedCriteria.length === 0) && Array.isArray(essay.consolidatedMarkingResults)) {
          consolidatedCriteria = essay.consolidatedMarkingResults;
        }
        if ((!consolidatedCriteria || consolidatedCriteria.length === 0) && rawCriteriaResults.length > 0) {
          consolidatedCriteria = deriveConsolidatedFromRaw(rawCriteriaResults);
        }

        // Build a deterministic criterion order for summary
        const criterionOrder = Array.isArray(assessment.criteria) && assessment.criteria.length > 0
          ? assessment.criteria.map((c: any) => c.name)
          : consolidatedCriteria.map((c: any) => c.criterionName);
        const summaryCriteria: Array<{ label: string; aiBand: string; teacherBand: string }> = [];

        consolidatedCriteria.forEach((crit: any) => {
          const rawCrit = rawCriteriaResults.find((r: any) => normalizeCriterionName(r.criterionName) === normalizeCriterionName(crit.criterionName));
          const manualCrit = (essay.manualMarkingResults || []).find((m: any) => normalizeCriterionName(m.criterionName) === normalizeCriterionName(crit.criterionName));

          const aiBandLabel = crit.selectedBand || rawCrit?.selectedBand || '';
          const teacherBandLabel = manualCrit?.band ?? '';
          const label = crit.criterionName || 'Criterion';
          summaryCriteria.push({
            label,
            aiBand: aiBandLabel,
            teacherBand: teacherBandLabel,
          });

          const bandAnalysis = Array.isArray(rawCrit?.bandAnalysis) && rawCrit.bandAnalysis.length > 0
            ? rawCrit.bandAnalysis
            : [null];

          bandAnalysis.forEach((band: any) => {
            rows.push({
              assessmentId: assessment.id,
              assessmentName: assessmentLabel,
              assessmentNumber: assessment.assessmentNumber ?? '',
              essayId: essay.id,
              studentName: essay.studentName || '',
              modelId,
              modelName: modelConfig?.name || modelId,
              modelVersion: modelResult.modelVersion || modelConfig?.version || '',
              promptVersion: modelResult.promptVersion || '',
              aiOverallScore: modelResult.finalOverallScore ?? '',
              aiOverallBand: modelResult.finalOverallBand ?? '',
              aiFeedback: modelResult.feedback || '',
              teacherOverallScore: essay.manualOverallScore ?? '',
              aiMarkedAt: formatDate(modelResult.lastRunAt || essay.markedAt),
              essayCreatedAt: formatDate(essay.createdAt),
              criterionName: crit.criterionName,
              aiSelectedBand: crit.selectedBand || rawCrit?.selectedBand || '',
              aiCriterionScore: crit.score ?? rawCrit?.score ?? '',
              aiJustification: crit.justification || rawCrit?.justification || '',
              aiWhyHigherNot: rawCrit?.justificationBandAbove || '',
              aiWhyLowerNot: rawCrit?.justificationBandBelow || '',
              bandName: band?.bandName || '',
              aiMatchScore: band?.matchScore ?? '',
              aiConfidenceScore: band?.confidenceScore ?? '',
              teacherBand: manualCrit?.band ?? '',
              teacherJustification: manualCrit?.justification ?? '',
            });
          });
        });

        summaryRows.push({
          assessmentId: assessment.id,
          assessmentName: assessmentLabel,
          assessmentNumber: assessment.assessmentNumber ?? '',
          essayId: essay.id,
          studentName: essay.studentName || '',
          modelId,
          modelName: modelConfig?.name || modelId,
          aiOverallScore: modelResult.finalOverallScore ?? '',
          aiOverallBand: modelResult.finalOverallBand ?? '',
          teacherOverallScore: essay.manualOverallScore ?? '',
          difference: (modelResult.finalOverallScore ?? 0) - (essay.manualOverallScore ?? 0),
          criteriaBands: criterionOrder.map((name, idx) => {
            const entry = summaryCriteria.find((c) => normalizeCriterionName(c.label) === normalizeCriterionName(name));
            return entry || { label: name || `Criterion ${idx + 1}`, aiBand: '', teacherBand: '' };
          }),
        });
      }
    }
  }

  return {
    rows,
    summaryRows,
    modelIdsUsed: Array.from(modelIdsUsed),
    assessmentNames: selectedAssessments.map((a: any) => a.name || a.title || a.id),
  };
};

const buildCsvSnapshotData = async (
  userId: string,
  assessmentIds: string[],
  selectedModelId?: string,
  onStatus?: (msg: string) => void
) => {
  const allAssessments = await getAllAssessments(userId);
  const selectedAssessments = (allAssessments || []).filter((a: any) => assessmentIds.includes(a.id));

  const summaryRows: any[] = [];
  const modelIdsUsed = new Set<string>();

  if (selectedModelId) {
    modelIdsUsed.add(selectedModelId);
  }

  for (const assessment of selectedAssessments) {
    const assessmentLabel = buildAssessmentLabel(assessment);
    onStatus?.(`Fetching essays for ${assessmentLabel}...`);
    const essays = await getEssaysForAssessment(userId, assessment.id);

    for (const essay of essays || []) {
      const { modelResult, modelId } = resolveModelResult(essay, selectedModelId);
      if (modelResult && modelId) {
        modelIdsUsed.add(modelId);
      }

      let consolidatedCriteria = Array.isArray(modelResult?.consolidatedMarkingResults)
        ? modelResult.consolidatedMarkingResults
        : [];
      const rawRuns = Array.isArray(modelResult?.rawMarkingRuns) ? modelResult.rawMarkingRuns : Array.isArray(essay.rawMarkingRuns) ? essay.rawMarkingRuns : [];
      const rawCriteriaResults = getLatestCriteriaResults(rawRuns);
      if ((!consolidatedCriteria || consolidatedCriteria.length === 0) && Array.isArray(essay.consolidatedMarkingResults)) {
        consolidatedCriteria = essay.consolidatedMarkingResults;
      }
      if ((!consolidatedCriteria || consolidatedCriteria.length === 0) && rawCriteriaResults.length > 0) {
        consolidatedCriteria = deriveConsolidatedFromRaw(rawCriteriaResults);
      }
      const manualResults = Array.isArray(essay.manualMarkingResults) ? essay.manualMarkingResults : [];
      const criterionOrder = getCriterionOrder(assessment, consolidatedCriteria, manualResults);
      const criteriaBands = buildCriteriaBands({
        criterionOrder,
        consolidatedCriteria,
        rawCriteriaResults,
        manualResults,
        limit: CSV_CRITERIA_LIMIT,
      });

      const aiOverallScore = formatSnapshotValue(modelResult?.finalOverallScore);
      const teacherOverallScore = formatSnapshotValue(essay.manualOverallScore);
      const difference = computeDifference(modelResult?.finalOverallScore, essay.manualOverallScore);

      const rowObj: any = {
        Assessment: assessmentLabel,
        Essay: buildEssayLabel(essay),
        'AI Overall Score': aiOverallScore,
        'Teacher Overall Score': teacherOverallScore,
        Difference: difference,
      };

      for (let i = 0; i < CSV_CRITERIA_LIMIT; i++) {
        const entry = criteriaBands[i] || { aiBand: CSV_NA_VALUE, teacherBand: CSV_NA_VALUE };
        rowObj[`Criterion ${i + 1} AI Score`] = entry.aiBand;
        rowObj[`Criterion ${i + 1} Teacher Score`] = entry.teacherBand;
      }

      summaryRows.push(rowObj);
    }
  }

  return {
    summaryRows,
    modelIdsUsed: Array.from(modelIdsUsed),
    assessmentNames: selectedAssessments.map((a: any) => buildAssessmentLabel(a)),
  };
};

const downloadWorkbook = (workbook: XLSX.WorkBook, filename: string) => {
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildCsvHeaders = (criteriaLimit: number = CSV_CRITERIA_LIMIT) => {
  const headers = [
    'Assessment',
    'Essay',
    'AI Overall Score',
    'Teacher Overall Score',
    'Difference',
  ];
  for (let i = 0; i < criteriaLimit; i++) {
    headers.push(`Criterion ${i + 1} AI Score`);
    headers.push(`Criterion ${i + 1} Teacher Score`);
  }
  return headers;
};

const buildSummaryCsv = (summaryRows: any[], criteriaLimit: number = CSV_CRITERIA_LIMIT) => {
  const headers = buildCsvHeaders(criteriaLimit);
  const sheet = XLSX.utils.json_to_sheet(summaryRows, { header: headers, skipHeader: false });
  return XLSX.utils.sheet_to_csv(sheet, { FS: ',', RS: '\r\n' });
};

const toSheet = (rows: any[], header: string[]) => XLSX.utils.json_to_sheet(rows, { header, skipHeader: false });

const buildWorkbook = (rows: any[], summaryRows: any[]) => {
  const wb = XLSX.utils.book_new();

  // Summary sheet first
  const maxCriteria = summaryRows.reduce((max, row) => Math.max(max, (row.criteriaBands || []).length), 0);
  const summaryHeaders = [
    'Assessment',
    'Essay',
    'AI Overall Score',
    'Teacher Overall Score',
    'Difference',
  ];
  for (let i = 0; i < maxCriteria; i++) {
    summaryHeaders.push(`Criterion ${i + 1} AI Score`);
    summaryHeaders.push(`Criterion ${i + 1} Teacher Score`);
  }
  const summarySheetRows = summaryRows.map((row) => {
    const criterionBands = row.criteriaBands || [];
    const rowObj: any = {
      Assessment: row.assessmentName,
      Essay: row.essayId,
      'AI Overall Score': row.aiOverallScore,
      'Teacher Overall Score': row.teacherOverallScore,
      Difference: row.difference ?? '',
    };
    for (let i = 0; i < maxCriteria; i++) {
      const entry = criterionBands[i];
      rowObj[`Criterion ${i + 1} AI Score`] = entry?.aiBand || '';
      rowObj[`Criterion ${i + 1} Teacher Score`] = entry?.teacherBand || '';
    }
    return rowObj;
  });
  const summarySheet = XLSX.utils.json_to_sheet(summarySheetRows, { header: summaryHeaders });
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  XLSX.utils.book_append_sheet(
    wb,
    toSheet(rows, [
      'assessmentId',
      'assessmentName',
      'assessmentNumber',
      'essayId',
      'studentName',
      'modelId',
      'modelName',
      'modelVersion',
      'promptVersion',
      'aiOverallScore',
      'aiOverallBand',
      'aiFeedback',
      'teacherOverallScore',
      'aiMarkedAt',
      'essayCreatedAt',
      'criterionName',
      'aiSelectedBand',
      'aiCriterionScore',
      'aiJustification',
      'aiWhyHigherNot',
      'aiWhyLowerNot',
      'bandName',
      'aiMatchScore',
      'aiConfidenceScore',
      'teacherBand',
      'teacherJustification',
    ]),
    'Results'
  );
  return wb;
};

export const createSnapshotAndDownload = async (
  userId: string,
  assessmentIds: string[],
  triggeredBy: string,
  onStatus?: (msg: string) => void,
  options?: SnapshotExportOptions
) => {
  const exportFormat = options?.format === 'xlsx' ? 'xlsx' : 'csv';

  if (exportFormat === 'xlsx') {
    onStatus?.('Building snapshot data...');
    const { rows, summaryRows, modelIdsUsed, assessmentNames } = await buildSnapshotData(userId, assessmentIds, onStatus);

    const metaPayload = {
      userId,
      assessmentIds,
      assessmentNames,
      modelIds: modelIdsUsed,
      rowCount: rows.length,
      triggeredBy,
      createdAt: serverTimestamp(),
      format: 'xlsx',
    };

    let snapshotId = `local-${Date.now()}`;
    try {
      const docRef = await addDoc(snapshotsCollection, metaPayload);
      snapshotId = docRef.id;
    } catch (err: any) {
      console.warn('Failed to save snapshot metadata to Firestore. Proceeding with download only.', err);
      onStatus?.('Could not save snapshot metadata (check Firestore rules). Downloading locally only...');
    }

    const wb = buildWorkbook(rows, summaryRows);
    downloadWorkbook(wb, `snapshot-${snapshotId}.xlsx`);

    return { snapshotId, rowCount: rows.length };
  }

  const selectedModelId = options?.selectedModelId || DEFAULT_MODEL_ID;
  onStatus?.('Building CSV snapshot data...');
  const { summaryRows, modelIdsUsed, assessmentNames } = await buildCsvSnapshotData(
    userId,
    assessmentIds,
    selectedModelId,
    onStatus
  );

  const metaPayload = {
    userId,
    assessmentIds,
    assessmentNames,
    modelIds: modelIdsUsed,
    rowCount: summaryRows.length,
    triggeredBy,
    createdAt: serverTimestamp(),
    selectedModelId,
    format: 'csv',
  };

  let snapshotId = `local-${Date.now()}`;
  try {
    const docRef = await addDoc(snapshotsCollection, metaPayload);
    snapshotId = docRef.id;
  } catch (err: any) {
    console.warn('Failed to save snapshot metadata to Firestore. Proceeding with download only.', err);
    onStatus?.('Could not save snapshot metadata (check Firestore rules). Downloading locally only...');
  }

  const csv = buildSummaryCsv(summaryRows);
  downloadCsv(csv, `snapshot-${snapshotId}.csv`);

  return { snapshotId, rowCount: summaryRows.length };
};

export const listSnapshots = async (userId: string): Promise<SnapshotMeta[]> => {
  try {
    const q = query(snapshotsCollection, where('userId', '==', userId), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as SnapshotMeta[];
  } catch (err) {
    console.warn('Failed to list snapshots; returning empty array.', err);
    // Fallback without ordering if index missing
    try {
      const snap = await getDocs(query(snapshotsCollection, where('userId', '==', userId)));
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as SnapshotMeta[];
      return items.sort((a, b) => {
        const aDate = toDate(a.createdAt)?.getTime() || 0;
        const bDate = toDate(b.createdAt)?.getTime() || 0;
        return bDate - aDate;
      });
    } catch (innerErr) {
      console.warn('Fallback snapshot listing also failed; returning [].', innerErr);
      return [];
    }
  }
};

export const deleteSnapshot = async (snapshotId: string) => {
  await deleteDoc(doc(db, 'snapshots', snapshotId));
};

export const downloadSnapshotById = async (
  snapshotId: string,
  userId: string,
  onStatus?: (msg: string) => void
) => {
  const snapList = await listSnapshots(userId);
  const meta = snapList.find((s) => s.id === snapshotId);
  if (!meta) {
    throw new Error('Snapshot not found or access denied.');
  }
  const exportFormat = meta.format === 'csv' ? 'csv' : 'xlsx';

  if (exportFormat === 'csv') {
    const selectedModelId = meta.selectedModelId || meta.modelIds?.[0] || DEFAULT_MODEL_ID;
    onStatus?.('Regenerating CSV snapshot...');
    const { summaryRows } = await buildCsvSnapshotData(userId, meta.assessmentIds, selectedModelId, onStatus);
    const csv = buildSummaryCsv(summaryRows);
    downloadCsv(csv, `snapshot-${snapshotId}.csv`);
    return;
  }

  onStatus?.('Regenerating snapshot...');
  const { rows, summaryRows } = await buildSnapshotData(userId, meta.assessmentIds, onStatus);
  const wb = buildWorkbook(rows, summaryRows);
  downloadWorkbook(wb, `snapshot-${snapshotId}.xlsx`);
};
